import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import { CommentItem } from "./CommentItem";
import { useDispatch } from "react-redux";
import { ListDiscussionVideoLesson } from "../../../../Redux/Slices/UserVideoLessons/ListDiscussionVideoLessons";
import { AddDiscussionVideoLesson } from "../../../../Redux/Slices/UserVideoLessons/AddDiscussionVideoLessons";
import { EditDiscussionVideoLessons } from "../../../../Redux/Slices/UserVideoLessons/EditDiscussionVideoLessons";
import { DeleteDiscussionVideoLessons } from "../../../../Redux/Slices/UserVideoLessons/DeleteDiscussionVideoLessons";
import { useAppSelector } from "../../../../Redux/Store/Hooks";
import { LikeDiscussionVideoLesson } from "../../../../Redux/Slices/UserVideoLessons/LikeDiscussionVideoLessons";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import "./discussion.scss";
interface CommentType {
  id: number;
  video_id: number;
  student_id: number;
  comment: string;
  parent_id: number | null;
  created_on: string;
  updated_on?: string;
  children: CommentType[];
  likes_count: number;
  is_user_liked: boolean;
  is_user_commented: boolean;
}

export default function Discussion({ videoId }: { videoId: number }) {
  const dispatch = useDispatch();
  const { data: ListDiscussionVideo } = useAppSelector(
    (state) => state.ListDiscussionVideoLessonsSliceReducer
  );

  const [optimisticComments, setOptimisticComments] = useState<CommentType[]>(
    []
  );
  const comments = ListDiscussionVideo?.data || [];

  const allComments = useMemo(() => {
    if (comments.length > 0 && optimisticComments.length > 0) {
      const hasMatchingApiComment = optimisticComments.some(
        (opt: CommentType) =>
          comments.some(
            (api: CommentType) =>
              api.comment === opt.comment &&
              Math.abs(
                new Date(api.created_on).getTime() -
                  new Date(opt.created_on).getTime()
              ) < 5000
          )
      );

      if (hasMatchingApiComment) {
        setTimeout(() => setOptimisticComments([]), 0);
        return comments;
      }
    }

    

    const combinedComments = JSON.parse(JSON.stringify(comments));

    const findCommentById = (
      comments: CommentType[],
      id: number
    ): CommentType | null => {
      for (const comment of comments) {
        if (comment.id === id) return comment;
        if (comment.children.length > 0) {
          const found = findCommentById(comment.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    optimisticComments.forEach((oc) => {
      const existsById = findCommentById(combinedComments, oc.id);
      const existsByContent = combinedComments.some(
        (c: CommentType) =>
          c.comment === oc.comment &&
          Math.abs(
            new Date(c.created_on).getTime() - new Date(oc.created_on).getTime()
          ) < 5000
      );

      if (!existsById && !existsByContent) {
        if (oc.parent_id === null) {
          combinedComments.unshift(oc);
        } else {
          const parent = findCommentById(combinedComments, oc.parent_id);
          if (
            parent &&
            !parent.children.some(
              (c) => c.id === oc.id || c.comment === oc.comment
            )
          ) {
            parent.children.push(oc);
          }
        }
      }
    });

    return combinedComments;
  }, [comments, optimisticComments]);

  const [newComment, setNewComment] = useState("");
  const [replyInputOpen, setReplyInputOpen] = useState<{
    [key: number]: boolean;
  }>({});
  const [replyTexts, setReplyTexts] = useState<{ [key: number]: string }>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [showReplies, setShowReplies] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [menuComment, setMenuComment] = useState<CommentType | null>(null);

  useEffect(() => {
    dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
  }, [dispatch]);

  useEffect(() => {
    if (comments.length > 0) {
      setOptimisticComments((prev) => {
        return prev.filter((optimistic) => {
          const existsInApi = comments.some((apiComment: CommentType) => {
            const isSameComment =
              apiComment.comment === optimistic.comment &&
              Math.abs(
                new Date(apiComment.created_on).getTime() -
                  new Date(optimistic.created_on).getTime()
              ) < 5000;

            return (
              apiComment.id === optimistic.id ||
              isSameComment ||
              findCommentInChildren(apiComment, optimistic.id)
            );
          });
          return !existsInApi;
        });
      });
    }
  }, [comments]);

  const findCommentInChildren = (comment: CommentType, id: number): boolean => {
    if (comment.id === id) return true;
    return comment.children.some((child) => findCommentInChildren(child, id));
  };

  const toggleCommentReplies = (commentId: number) => {
    setShowReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleAddReply = async (parentId: number, text: string) => {
    if (!text.trim()) return;

    const tempReply: CommentType = {
      id: -Date.now(),
      video_id: videoId,
      student_id: 282,
      comment: text,
      parent_id: parentId,
      created_on: new Date().toISOString(),
      children: [],
      likes_count: 0,
      is_user_liked: false,
      is_user_commented: true,
    };

    try {
      setOptimisticComments((prev) => [...prev, tempReply]);
      setReplyTexts((prev) => ({ ...prev, [parentId]: "" }));
      setReplyInputOpen((prev) => ({ ...prev, [parentId]: false }));

      await dispatch(
        AddDiscussionVideoLesson({
          video_id: videoId,
          comment: text,
          parent_id: parentId,
          is_deleted: false,
        })
      );

      setTimeout(() => {
        dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
        setOptimisticComments([]);
      }, 300);
    } catch (error) {
      setOptimisticComments((prev) =>
        prev.filter((c) => String(c.id) !== String(tempReply.id))
      );
      console.error("Failed to add reply:", error);
    }
  };

  const handleToggleReplyInput = (id: number) => {
    setReplyInputOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNewCommentPost = async () => {
    if (!newComment.trim()) return;

    const tempComment: CommentType = {
      id: -Date.now(),
      video_id: videoId,
      student_id: 282,
      comment: newComment,
      parent_id: null,
      created_on: new Date().toISOString(),
      children: [],
      likes_count: 0,
      is_user_liked: false,
      is_user_commented: true,
    };

    const commentText = newComment;
    setNewComment("");

    try {
      setOptimisticComments((prev) => [tempComment, ...prev]);

      await dispatch(
        AddDiscussionVideoLesson({
          video_id: videoId,
          comment: commentText,
          parent_id: null,
          is_deleted: false,
        })
      );

      setTimeout(() => {
        dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
        setOptimisticComments([]);
      }, 300);
    } catch (error) {
      setOptimisticComments((prev) =>
        prev.filter((c) => c.id !== tempComment.id)
      );
      setNewComment(commentText);
      console.error("Failed to add comment:", error);
    }
  };

  const handleCommentMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    comment: CommentType
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuComment(comment);
  };

  const handleCommentMenuClose = () => {
    setAnchorEl(null);
    setMenuComment(null);
  };

  const handleEditComment = () => {
    if (menuComment) {
      setEditingCommentId(menuComment.id);
      setEditCommentText(menuComment.comment);
    }
    handleCommentMenuClose();
  };

  const handleSaveEditedComment = async () => {
    if (editingCommentId && editCommentText.trim()) {
      try {
        await dispatch(
          EditDiscussionVideoLessons({
            id: editingCommentId,
            comment: editCommentText,
            video_id: videoId,
          })
        );

        // Clear editing state immediately
        setEditingCommentId(null);
        setEditCommentText("");

        setTimeout(() => {
          dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
        }, 300);
      } catch (error) {
        console.error("Failed to update comment:", error);
      }
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      // Remove from optimistic comments immediately
      setOptimisticComments((prev) => prev.filter((c) => c.id !== commentId));

      await dispatch(DeleteDiscussionVideoLessons({ comment_id: commentId }));

      // Clear all related states
      setMenuComment(null);
      setAnchorEl(null);
      setEditingCommentId(null);
      setEditCommentText("");
      setReplyInputOpen((prev) => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });
      setReplyTexts((prev) => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });
      setShowReplies((prev) => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });

      setTimeout(() => {
        dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
      }, 300);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      // Refresh on error to ensure consistency
      dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
    }
  };

  const handleEditReply = async (id: number, newText: string) => {
    try {
      await dispatch(
        EditDiscussionVideoLessons({
          id,
          comment: newText,
          video_id: videoId,
        })
      );
      setTimeout(() => {
        dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
      }, 300);
    } catch (error) {
      console.error("Failed to update reply:", error);
    }
  };

  const handleDeleteReply = async (id: number) => {
    try {
      // Remove from optimistic comments immediately
      setOptimisticComments((prev) => prev.filter((c) => c.id !== id));

      await dispatch(DeleteDiscussionVideoLessons({ comment_id: id }));

      // Clear related states for the reply
      setReplyInputOpen((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      setReplyTexts((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });

      setTimeout(() => {
        dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
      }, 300);
    } catch (error) {
      console.error("Failed to delete reply:", error);
      // Refresh on error to ensure consistency
      dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
    }
  };

// In your Discussion component, update the handleLikeComment function:
const handleLikeComment = (commentId: number) => {
  const comment = findCommentById(allComments, commentId);
  if (!comment) return;

  // Optimistically update the UI
  setOptimisticComments(prev => 
    prev.map(c => 
      c.id === commentId 
        ? { 
            ...c, 
            is_user_liked: !c.is_user_liked,
            likes_count: c.is_user_liked ? c.likes_count - 1 : c.likes_count + 1
          } 
        : c
    )
  );

  dispatch(
    LikeDiscussionVideoLesson({
      comment_id: commentId,
      is_liked: !comment.is_user_liked,
    })
  );

  setTimeout(() => {
    dispatch(ListDiscussionVideoLesson({ video_id: videoId }));
  }, 100);
};

// Add this helper function to find comments by ID
const findCommentById = (comments: CommentType[], id: number): CommentType | null => {
  for (const comment of comments) {
    if (comment.id === id) return comment;
    if (comment.children.length > 0) {
      const found = findCommentById(comment.children, id);
      if (found) return found;
    }
  }
  return null;
};

  return (
    <Box
      sx={{
        width: "100%",
         height:"100%",
          maxHeight:"800px",
        mx: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        overflow: "scroll",
        background: "#ffff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        "& .MuiButton-root": {
          textTransform: "none",
          fontSize: "0.875rem",
          padding: "4px 8px",
        },
        "& .MuiTypography-caption": {
          ml: 0.5,
          mr: 1.5,
        },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          display={"flex"}
          alignItems={"center"}
          fontSize={"24px"}
          gap={"5px"}
          lineHeight={"24px"}
          fontWeight={"600"}
          color={"#020817"}
          paddingBottom={"1.5rem"}
        >
          <ChatBubbleOutlineOutlinedIcon />
          Discussion ({allComments.length})
        </Typography>
        <Typography color={"#020817"}>
          <TextField
            multiline
            minRows={2}
            fullWidth
            className="ask-question"
            placeholder="Share your thoughts or ask a question..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{
              mb: 1,
              border: "none",
              fontSize: "14px",
              lineHeight: "20px",
              color: "#020817",
              "& .MuiOutlinedInput-root": {
                border: "1px solid #e2e8f0 !important",
                borderRadius: "8px", // optional
                "& fieldset": {
                  border: "none", // remove default
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
        </Typography>
        <Typography display={"flex"} justifyContent={"flex-end"}>
          <Button
            variant="contained"
            className="post-command"
            onClick={handleNewCommentPost}
            disabled={!newComment.trim()}
            sx={{
              "&.Mui-disabled": {
                background: "#7faaff",
                color: "#ffff",
              },
            }}
          >
            Post Comment
          </Button>
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p:2,
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "3px",
          },
        }}
      >
        {allComments.map((comment: any) => (
          <CommentItem
            key={`comment-${comment.id}-${comment.created_on}`}
            comment={comment}
            onDeleteComment={handleDeleteComment}
            onDeleteReply={handleDeleteReply}
            onAddReply={handleAddReply}
            onToggleReplyInput={handleToggleReplyInput}
            onToggleReplies={toggleCommentReplies}
            replyInputOpen={replyInputOpen[comment.id] || false}
            showReplies={showReplies[comment.id] || false}
            replyText={replyTexts[comment.id] || ""}
            onReplyTextChange={(id, text) =>
              setReplyTexts((prev) => ({ ...prev, [id]: text }))
            }
            editingCommentId={editingCommentId}
            editCommentText={editCommentText}
            onEditCommentTextChange={setEditCommentText}
            onSaveEditedComment={handleSaveEditedComment}
            onCancelEdit={() => {
              setEditingCommentId(null);
              setEditCommentText("");
            }}
            anchorEl={anchorEl}
            onMenuClick={(event) => handleCommentMenuClick(event, comment)}
            onMenuClose={handleCommentMenuClose}
            onEditClick={handleEditComment}
            onEdit={handleEditReply}
            onLikeComment={handleLikeComment}
            onLikeReply={handleLikeComment}
            menuComment={menuComment}
          />
        ))}
      </Box>
    </Box>
  );
}
